import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const SERVER_KEYS = ["mask", "alpha_job", "alpha_error"];

// Clean Plate session persisted on the server so a refresh restores everything.
export default function useCpSession(projectId, tool = "cleanplate") {
  const [st, setSt] = useState(null);
  const timer = useRef(null);
  const latest = useRef(null);
  const dead = useRef(false);
  const pending = useRef(null);

  const pull = useCallback(() => api.get("/bfx/cleanplate/session", { params: { project_id: projectId, tool } })
    .then((r) => r.data.state || {}), [projectId, tool]);

  useEffect(() => {
    let live = true;
    pending.current = [];
    const boot = (s) => {
      if (!live) return;
      const queued = pending.current || [];
      pending.current = null;
      const next = queued.reduce((acc, p) => ({ ...acc, ...(typeof p === "function" ? p(acc) : p) }), s);
      latest.current = next;
      setSt(next);
      if (queued.length) { clearTimeout(timer.current); timer.current = setTimeout(() => flushRef.current(), 500); }
    };
    pull().then(boot).catch(() => boot({}));
    return () => { live = false; };
  }, [pull]);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const s = latest.current;
    if (!s || dead.current) return;
    const body = Object.fromEntries(Object.entries(s).filter(([k]) => !SERVER_KEYS.includes(k)));
    api.put("/bfx/cleanplate/session", { project_id: projectId, state: body, tool }).catch(() => {});
  }, [projectId, tool]);

  const flushRef = useRef(flush);
  flushRef.current = flush;

  const patch = useCallback((p) => {
    if (dead.current) return;
    if (pending.current) { pending.current.push(p); return; }
    setSt((cur) => {
      const next = { ...(cur || {}), ...(typeof p === "function" ? p(cur || {}) : p) };
      latest.current = next;
      return next;
    });
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 500);
  }, [flush]);

  useEffect(() => () => flush(), [flush]);
  useEffect(() => {
    const onReset = () => { clearTimeout(timer.current); latest.current = null; dead.current = true; };
    window.addEventListener("ax-cp-reset", onReset);
    return () => window.removeEventListener("ax-cp-reset", onReset);
  }, []);

  const setMask = useCallback(async (mask, alphaJob = null) => {
    if (dead.current) return;
    setSt((cur) => { const next = { ...(cur || {}), mask, alpha_job: alphaJob, alpha_error: null }; latest.current = next; return next; });
    await api.put("/bfx/cleanplate/session/mask", { project_id: projectId, mask, alpha_job: alphaJob, tool });
  }, [projectId, tool]);

  const waiting = !!st?.alpha_job;
  useEffect(() => {
    const onRefresh = () => pull().then((s) => setSt((cur) => {
      const next = { ...(cur || {}), ...Object.fromEntries(SERVER_KEYS.map((k) => [k, s[k] ?? null])) };
      latest.current = next;
      return next;
    })).catch(() => {});
    window.addEventListener("ax-cp-refresh", onRefresh);
    return () => window.removeEventListener("ax-cp-refresh", onRefresh);
  }, [pull]);
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(() => pull().then((s) => setSt((cur) => {
      const next = { ...(cur || {}), ...Object.fromEntries(SERVER_KEYS.map((k) => [k, s[k] ?? null])) };
      latest.current = next;
      return next;
    })).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [waiting, pull]);

  return { st, patch, setMask };
}

export const tagOf = (ref) => (ref?.name || "").replace(/\.[^.]+$/, "").replace(/[^\w.-]+/g, "_");

// Keep @tags for the given names in the text: add missing ones in front, drop ones that were removed.
export const syncTags = (text, tags, prevTags) => {
  let t = text || "";
  prevTags.filter((x) => !tags.includes(x)).forEach((x) => { t = t.replace(new RegExp(`@${x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\w.-]) ?`, "g"), ""); });
  const missing = tags.filter((x) => !new RegExp(`@${x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\w.-])`).test(t));
  if (missing.length) t = `${missing.map((x) => `@${x}`).join(" ")} ${t}`;
  return t.replace(/ {2,}/g, " ");
};

export const useTagSync = (value, setValue, tags, ready) => {
  const prev = useRef(null);
  const key = tags.join("|");
  useEffect(() => {
    if (!ready) return;
    const before = prev.current ?? [];
    prev.current = tags;
    const next = syncTags(value, tags, before);
    if (next !== value) setValue(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready]);
};
